import React, { FunctionComponent } from 'react'
import { PuxWysiwygHtmlBodyType } from 'Shared/components/PuxWysiwyg/PuxWysiwyg'
import { getLocalizedData } from 'Shared/utils/localeURL'

import { MediaSelectorImageType } from '../components/content/mediaImage/PuxPicture'
import { localizedPathType } from '../components/PuxLanguageSelector/PuxLanguageSelector'
import getHrefLangsForHelmet, {
  getShortHrefLang,
} from './getHreflangsForHelmet'
import {
  getStructuredDataScriptContent,
  mergeStructuredData,
  StructuredDataInput,
} from './structuredData'

const getFilteredPathQuery = (): string => {
  if (typeof window === `undefined`) {
    return ``
  }

  const searchParams = new URLSearchParams(window.location.search)
  const filteredParams = new URLSearchParams()

  searchParams.forEach((value, key) => {
    if (key === `page` || key.startsWith(`filter-`)) {
      filteredParams.append(key, value)
    }
  })

  const result = filteredParams.toString()
  return result ? `?${result}` : ``
}

const localizedConfigData = getLocalizedData(
  process.env.LOCALE ?? ``,
  process.env.PROJECT ?? ``
)
const activeEnv = process.env.GATSBY_ACTIVE_ENV ?? ``

export interface PuxMetaTagsType {
  metaTagsTitle?: PuxWysiwygHtmlBodyType
  metaTagsRobots?: string
  metaTagsCanonicalUrl?: PuxWysiwygHtmlBodyType
  metaTagsKeywords?: PuxWysiwygHtmlBodyType
  metaTagsDescription?: PuxWysiwygHtmlBodyType
  metaTagsOGTitle?: PuxWysiwygHtmlBodyType
  metaTagsOGDescription?: PuxWysiwygHtmlBodyType
  metaTagsOGImage?: MediaSelectorImageType
  metaTagsImage?: MediaSelectorImageType
  metaTagsStructuredData?: PuxWysiwygHtmlBodyType
  structuredData?: StructuredDataInput | StructuredDataInput[]
  localizedPaths: localizedPathType[]
  metaTagsPostfix?: boolean
}

const PuxMetaTags: FunctionComponent<PuxMetaTagsType> = (props) => {
  const canonicalPath =
    localizedConfigData.domains[activeEnv] + props.metaTagsCanonicalUrl?.html
  const pathQuery = getFilteredPathQuery()
  let metaTagsOGTitle = props.metaTagsOGTitle
  const metaTagsTitle = props.metaTagsTitle
  let metaTagsOGDescription = props.metaTagsOGDescription
  const metaTagsDescription = props.metaTagsDescription
  /* eslint-disable */
  if (!metaTagsOGTitle?.html) {
    metaTagsOGTitle = metaTagsTitle
  }

  if (!metaTagsOGDescription?.html) {
    metaTagsOGDescription = metaTagsDescription
  }

  const hreflangs = getHrefLangsForHelmet(props.localizedPaths ?? [])
  const structuredDataSources = Array.isArray(props.structuredData) ? props.structuredData : [props.structuredData]
  const structuredDataScriptContent = getStructuredDataScriptContent(
    mergeStructuredData(...structuredDataSources, props.metaTagsStructuredData)
  )

  var metapath = ``
  if (props.metaTagsOGImage?.resizePaths?.length) {
    metapath = props.metaTagsOGImage.resizePaths[0].substring(
      0,
      props.metaTagsOGImage.resizePaths[0].indexOf(`?`) ?? props.metaTagsOGImage.resizePaths[0].length
    )
  }

  return (
    <>
      {metaTagsTitle?.html && (
        <title>{metaTagsTitle.html.replace(/&amp;/g, '&')}</title>
      )}

      {props.metaTagsRobots && (
        <meta name='robots' content={props.metaTagsRobots} />
      )}

      <meta
        httpEquiv='content-language'
        content={getShortHrefLang(localizedConfigData.isoCode)}
      />

      {props.metaTagsCanonicalUrl?.html && (
        <link
          rel='canonical'
          href={
            canonicalPath.endsWith(`/`) ?
              canonicalPath.slice(0, -1) + pathQuery :
              canonicalPath + pathQuery
          }
        />
      )}

      {metaTagsDescription?.html && (
        <meta
          name='description'
          content={metaTagsDescription.html.replace(/(<([^>]+)>)/gi, ``)}
        />
      )}

      {props.metaTagsKeywords?.html && (
        <meta name='keywords' content={props.metaTagsKeywords.html} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Easy8com" />
      <meta name="twitter:creator" content="@Easy8com" />
      <meta property="og:type" content="company" />

      {metaTagsOGTitle?.html && (
        <meta
          property='og:title'
          content={metaTagsOGTitle.html.replace(/&amp;/g, '&')}
        />
      )}

      {metaTagsOGTitle?.html && (
        <meta
          name="twitter:title"
          content={metaTagsOGTitle.html.replace(/&amp;/g, '&')}
        />
      )}

      {metaTagsOGDescription?.html && (
        <meta
          property='og:description'
          content={metaTagsOGDescription.html.replace(/(<([^>]+)>)/gi, ``)}
        />
      )}

      {metaTagsOGDescription?.html && (
        <meta
          name="twitter:description"
          content={metaTagsOGDescription.html.replace(/(<([^>]+)>)/gi, ``)}
        />
      )
      }

      {
        metapath !== `` && (
          <meta
            property='og:image'
            content={metapath}
          />
        )
      }

      {
        !props.metaTagsOGImage?.resizePaths?.length && (
          <meta
            property='og:image'
            content={localizedConfigData.domains[activeEnv] + `/og-default.png`}
          />
        )
      }


      {hreflangs?.map((hreflang, index) => {
        return (
          <link
            rel='alternate'
            hrefLang={getShortHrefLang(hreflang.isoCode)}
            href={hreflang.path}
            key={index}
          />
        )
      })}
      {structuredDataScriptContent && (
        <script
          id='structured-data-pux-meta-tags'
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: structuredDataScriptContent,
          }}
        />
      )}
    </>
  )
  /* eslint-enable */
}

export default PuxMetaTags
